import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Sword, Heart, AlertTriangle, CheckCircle, XCircle, Loader2, Target, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { CustomsGate } from '../../nobel';
import { geminiEngine } from '../../../services/geminiEngine';
import { gamificationService } from '../../../services/gamificationService';
import { GrammarChallenge } from '../../../types';
import { FEEDBACK_ANIMATIONS } from '../../../utils/animations';

export const GrammarBattleground: React.FC = () => {
  const userId = sessionStorage.getItem('ielts_user_id') || '';
  const { canAccess, tier } = useFeatureAccess('advanced_module', userId);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const [challenge, setChallenge] = useState<GrammarChallenge | null>(null);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(3);
  const [round, setRound] = useState(1);
  const [streak, setStreak] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  useEffect(() => {
    if (!canAccess) {
      setPaywallOpen(true);
    } else {
      loadChallenge();
    }
  }, [canAccess]);

  const loadChallenge = async () => {
    setLoading(true);
    setSelectedOption(null);
    setIsCorrect(null);
    try {
      const difficulty = round % 5 === 0 ? 'hard' : 'medium'; // Boss battle every 5 rounds
      const data = await geminiEngine.generateGrammarChallenge(difficulty);
      setChallenge(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (option: string) => {
    if (selectedOption || !challenge) return;

    setSelectedOption(option);
    const correct = option === challenge.correctAnswer;
    setIsCorrect(correct);

    if (correct) {
      setStreak(s => s + 1);

      // Trigger success confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10b981', '#34d399', '#C5A059']
      });

      if (userId) {
        gamificationService.awardXP(userId, 'question_correct');
        if (streak > 0 && streak % 3 === 0) gamificationService.awardXP(userId, 'combo_5x');
      }

      // Auto-advance on correct after a brief pause
      setTimeout(() => {
        setRound(r => r + 1);
        loadChallenge();
      }, 2500);
    } else {
      setHealth(h => Math.max(0, h - 1));
      setStreak(0);
      if (userId) gamificationService.awardXP(userId, 'question_wrong');
    }
  };

  const restart = () => {
    setHealth(3);
    setRound(1);
    setStreak(0);
    loadChallenge();
  };

  if (health === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-red-950/20 rounded-2xl border-2 border-red-900/50 p-8 text-center">
        <XCircle className="w-24 h-24 text-red-500 mb-6" />
        <h2 className="text-4xl font-black text-white mb-2">MISSION FAILED</h2>
        <p className="text-stone-400 mb-8">You survived {round - 1} waves.</p>
        <button
          onClick={restart}
          className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl uppercase tracking-widest transition-all"
        >
          Respawn
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col bg-[#0f0f0f] rounded-2xl border border-stone-800 relative overflow-hidden">
        {/* Combat HUD */}
        <div className="bg-stone-900/80 p-6 border-b border-stone-800 flex justify-between items-center relative z-10">
          <div className="flex items-center gap-4">
            <div className="flex gap-1">
              {[1, 2, 3].map(i => (
                <motion.div
                  key={i}
                  animate={i > health ? { scale: [1, 1.2, 0], opacity: 0 } : { scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Heart
                    size={24}
                    className={i <= health ? "text-red-500 fill-red-500" : "text-stone-800"}
                    style={{ filter: i <= health ? 'drop-shadow(0 0 8px rgba(239,68,68,0.6))' : 'none' }}
                  />
                </motion.div>
              ))}
            </div>
            <div className="w-px h-8 bg-stone-700 mx-2"></div>
            <div className="text-stone-400 font-bold mono">WAVE {round}</div>
            {round % 5 === 0 && (
              <div className="px-3 py-1 bg-red-500/20 border border-red-500 text-red-400 text-[10px] font-black uppercase rounded animate-pulse ml-4">
                BOSS BATTLE
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[8px] mono text-stone-500 uppercase">COMBO</div>
              <div className="text-xl font-black text-[#C5A059]">{streak}x</div>
            </div>
            <Sword size={24} className="text-[#C5A059]" />
          </div>
        </div>

        {/* Battlefield */}
        <motion.div
          animate={isCorrect === false ? FEEDBACK_ANIMATIONS.error : {}}
          className="flex-1 flex flex-col items-center justify-center p-8 relative"
        >
          {/* Grid Background */}
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle, #333 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
          />

          {loading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="animate-spin text-[#C5A059]" size={48} />
              <p className="text-stone-500 mono text-xs uppercase tracking-widest">Scanning grammar syntax...</p>
            </div>
          ) : challenge ? (
            <div className="w-full max-w-3xl space-y-8 relative z-10">
              {/* Enemy Sentence */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-stone-900 border-2 border-red-500/30 p-8 rounded-2xl text-center shadow-[0_0_30px_rgba(239,68,68,0.1)] relative overflow-hidden"
              >
                <div className="flex items-center justify-center gap-2 mb-4 text-red-400 text-xs font-black mono uppercase">
                  <AlertTriangle size={14} /> Corruption Detected: {challenge.errorType}
                </div>
                <h2 className="text-2xl md:text-3xl font-serif text-stone-200 leading-relaxed">
                  "{challenge.sentence}"
                </h2>
              </motion.div>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {challenge.options.map((opt, idx) => {
                  let stateClass = 'bg-stone-800 border-stone-700 text-stone-400 hover:border-[#C5A059] hover:text-[#C5A059]';
                  if (selectedOption) {
                    if (opt === challenge.correctAnswer) stateClass = 'bg-emerald-900/30 border-emerald-500 text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.3)]';
                    else if (opt === selectedOption) stateClass = 'bg-red-900/30 border-red-500 text-red-400';
                    else stateClass = 'bg-stone-900/50 border-stone-800 text-stone-600 opacity-50';
                  }

                  return (
                    <motion.button
                      key={idx}
                      onClick={() => handleSelect(opt)}
                      disabled={!!selectedOption}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        y: (selectedOption && opt === challenge.correctAnswer) ? -5 : 0
                      }}
                      transition={{ delay: idx * 0.1 }}
                      className={`p-6 rounded-xl border-2 text-left transition-all relative overflow-hidden group ${stateClass}`}
                    >
                      <span className="relative z-10 font-medium">{opt}</span>
                      {selectedOption && opt === challenge.correctAnswer && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-4 right-4"
                        >
                          <CheckCircle className="text-emerald-500" size={20} />
                        </motion.div>
                      )}
                      {!selectedOption && (
                        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </motion.button>
                  );
                })}
              </div>

              {/* Feedback Panel */}
              <AnimatePresence>
                {selectedOption && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 30 }}
                    transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                    className={`rounded-2xl p-8 border-2 shadow-2xl relative overflow-hidden ${isCorrect
                        ? 'bg-emerald-950/40 border-emerald-500/50 shadow-emerald-500/10'
                        : 'bg-red-950/40 border-red-500/50 shadow-red-500/10'
                      }`}
                  >
                    {/* Cyber Accents */}
                    <div className={`absolute top-0 right-0 p-2 opacity-10 ${isCorrect ? 'text-emerald-500' : 'text-red-500'}`}>
                      <Target size={120} />
                    </div>

                    <div className="flex items-center gap-4 mb-4 relative z-10">
                      <div className={`p-3 rounded-xl ${isCorrect ? 'bg-emerald-500 text-stone-900' : 'bg-red-500 text-stone-900'}`}>
                        {isCorrect ? <CheckCircle size={24} /> : <XCircle size={24} />}
                      </div>
                      <div>
                        <h4 className={`text-xl font-black mono uppercase tracking-tight ${isCorrect ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isCorrect ? 'TARGET NEUTRALIZED' : 'BREACH DETECTED'}
                        </h4>
                        <div className="text-[10px] mono text-stone-500 uppercase">Analysis Complete</div>
                      </div>
                    </div>

                    <p className="text-stone-200 text-lg leading-relaxed mb-6 relative z-10 font-serif">
                      {challenge.explanation}
                    </p>

                    {!isCorrect && (
                      <button
                        onClick={loadChallenge}
                        className="relative z-10 w-full py-4 bg-stone-800 hover:bg-stone-700 border border-stone-600 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 group"
                      >
                        Next Target <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    )}

                    {isCorrect && (
                      <div className="flex items-center gap-2 text-emerald-500/60 mono text-[10px] uppercase font-bold relative z-10">
                        <Loader2 size={12} className="animate-spin" /> Synchronizing next wave...
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : null}
        </motion.div>
      </div>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature="Grammar Battleground"
        reason="premium_feature"
        currentTier={tier}
      />
    </>
  );
};

export default GrammarBattleground;